import { isEmploymentConfirmedStage } from "../types/career-outcome.js";
import { preferenceSalaryFloorMonthlyUsd } from "./compensation.js";
/**
 * "Better than your current job" for Radar alerts (specs/12 §4, decision D3).
 *
 * The baseline is the job the person confirmed in a `CareerOutcome`
 * (`temporary_work` / `employment_started`): the declared monthly pay of that
 * outcome or, without it, the posting's confirmed pay; the posting's
 * seniority (or the profile's) and its work mode. An A/B match is superior
 * when it pays at least {@link RADAR_SALARY_UPGRADE_RATIO} times the
 * baseline, or is more senior, or is in a preferred work mode the current
 * job is not in — the last two without paying less. Pure: the functions
 * package loads the documents and the FX table.
 */
/** "Superior" by pay alone: at least 15 % more than the baseline (D3). */
export const RADAR_SALARY_UPGRADE_RATIO = 1.15;
/** Only these grades are ever announced by Radar (D3). */
export const RADAR_ALERT_GRADES = ["A", "B"];
const SENIORITY_RANK = {
    entry: 0,
    junior: 0,
    mid: 1,
    senior: 2,
    staff: 3,
    lead: 3,
    principal: 4,
};
/**
 * Ordinal level of a stored `SeniorityBucket` or a profile `CareerSeniority`
 * (junior < mid < senior < lead < principal). Null when it carries no level.
 */
export function radarSeniorityRank(seniority) {
    if (typeof seniority !== "string")
        return null;
    return SENIORITY_RANK[seniority.trim().toLowerCase()] ?? null;
}
const RANK_BUCKET = ["junior", "mid", "senior", "lead", "principal"];
function seniorityBucket(seniority) {
    const rank = radarSeniorityRank(seniority);
    return rank == null ? null : RANK_BUCKET[rank];
}
function knownWorkMode(mode) {
    return mode === "remote" || mode === "hybrid" || mode === "onsite" ? mode : null;
}
function positive(value) {
    return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : null;
}
/**
 * Comparable monthly USD of a posting. Only `confirmed` amounts count: a
 * best-effort estimate (inferred period, assumed schedule) could be off by 12×
 * and must not decide that an offer pays more.
 */
export function radarConfirmedSalaryUsd(job) {
    if (!job || job.salaryComparableConfidence !== "confirmed")
        return null;
    return positive(job.salaryMonthlyUsd);
}
function latestEmploymentEnded(outcome) {
    const reviews = outcome.employmentReviews;
    const latest = reviews?.day90 ?? reviews?.day30 ?? reviews?.initial;
    return latest?.continuity === "ended";
}
function outcomeRecency(outcome) {
    const updated = outcome.updatedAt?.seconds;
    return [outcome.employmentStartedOn ?? "", typeof updated === "number" ? updated : 0];
}
/**
 * The application whose confirmed employment is the person's current job:
 * the latest start date (then the latest update) among confirmed outcomes,
 * skipping jobs the person reported as ended.
 */
export function pickRadarBaselineApplication(applications) {
    let best = null;
    let bestKey = ["", -1];
    for (const app of applications) {
        const outcome = app.careerOutcome;
        if (!outcome || !isEmploymentConfirmedStage(outcome.stage) || latestEmploymentEnded(outcome))
            continue;
        const key = outcomeRecency(outcome);
        if (best === null || key[0] > bestKey[0] || (key[0] === bestKey[0] && key[1] > bestKey[1])) {
            best = app;
            bestKey = key;
        }
    }
    return best;
}
/**
 * Monthly USD of a declared salary. `exchangeRates` is the usual table
 * (lowercase code → units per USD); USD needs none. Null when the currency
 * has no rate or the amount is implausible. The owner can write
 * `careerOutcome` directly (firestore.rules), so a malformed value is
 * treated as undeclared rather than trusted.
 */
export function declaredSalaryMonthlyUsd(salary, exchangeRates) {
    if (!salary || typeof salary.amount !== "number" || !(salary.amount > 0))
        return null;
    if (typeof salary.currency !== "string" || !/^[A-Z]{3}$/.test(salary.currency))
        return null;
    return positive(preferenceSalaryFloorMonthlyUsd(salary.amount, salary.currency, {
        exchangeRates: { ...(exchangeRates ?? {}), usd: 1 },
    }));
}
/**
 * Baseline from the confirmed outcome of `applicationId` and its posting.
 * Pay: the declared salary (converted with `exchangeRates`) wins over the
 * posting's confirmed pay. Seniority: the posting's, else the profile's.
 */
export function buildRadarBaseline(input) {
    const opp = input.opportunity ?? null;
    const declared = declaredSalaryMonthlyUsd(input.careerOutcome?.currentSalary, input.exchangeRates);
    const posted = declared == null ? radarConfirmedSalaryUsd(opp) : null;
    const company = opp?.company?.trim();
    const title = opp?.title?.trim();
    return {
        applicationId: input.applicationId,
        opportunityId: opp?.id ?? null,
        company: company || null,
        title: title || null,
        salaryMonthlyUsd: declared ?? posted,
        salarySource: declared != null ? "declared" : posted != null ? "posting" : null,
        seniority: seniorityBucket(opp?.seniority) ?? seniorityBucket(input.profileSeniority),
        workMode: knownWorkMode(opp?.workMode),
    };
}
/** A baseline with no pay, level or work mode has nothing to compare against. */
export function isComparableRadarBaseline(baseline) {
    return (baseline != null &&
        (positive(baseline.salaryMonthlyUsd) != null || baseline.seniority != null || baseline.workMode != null));
}
/**
 * D3. No comparable baseline (no confirmed job, or one we know nothing
 * about) → every A/B match counts (`no_baseline`). Otherwise the candidate is
 * superior when
 * - it pays ≥ baseline × {@link RADAR_SALARY_UPGRADE_RATIO} (both known), or
 * - its seniority is higher, or
 * - its work mode is one of `preferredWorkModes` and the baseline's is not,
 * and, for the last two, it does not pay less: with a known baseline pay the
 * candidate must publish a confirmed pay ≥ baseline. Without a baseline pay
 * there is nothing to compare, so only seniority and work mode decide.
 * The caller filters grades (A/B) before asking.
 */
export function evaluateRadarUpgrade(baseline, candidate, preferredWorkModes) {
    if (!isComparableRadarBaseline(baseline))
        return { superior: true, reason: "no_baseline" };
    const basePay = positive(baseline.salaryMonthlyUsd);
    const candidatePay = radarConfirmedSalaryUsd(candidate);
    if (basePay != null && candidatePay != null && candidatePay >= basePay * RADAR_SALARY_UPGRADE_RATIO) {
        return { superior: true, reason: "salary" };
    }
    const paysAtLeastAsMuch = basePay == null || (candidatePay != null && candidatePay >= basePay);
    if (!paysAtLeastAsMuch)
        return { superior: false, reason: null };
    const baseRank = radarSeniorityRank(baseline.seniority);
    const candidateRank = radarSeniorityRank(candidate.seniority);
    if (baseRank != null && candidateRank != null && candidateRank > baseRank) {
        return { superior: true, reason: "seniority" };
    }
    const preferred = new Set((preferredWorkModes ?? []).filter((mode) => knownWorkMode(mode)));
    const candidateMode = knownWorkMode(candidate.workMode);
    if (candidateMode != null &&
        baseline.workMode != null &&
        preferred.has(candidateMode) &&
        !preferred.has(baseline.workMode)) {
        return { superior: true, reason: "work_mode" };
    }
    return { superior: false, reason: null };
}
function count(value) {
    return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : 0;
}
/** Counters of `period` from a raw `radarAlerts` map (missing or malformed → zeros). */
export function readRadarAlertMonth(radarAlerts, period) {
    const month = radarAlerts && typeof radarAlerts === "object"
        ? radarAlerts[period]
        : undefined;
    return {
        alerts: count(month?.alerts),
        matches: count(month?.matches),
        delivered: count(month?.delivered),
    };
}
/**
 * Alerts per Radar user in `period` (`YYYY-MM`, UTC). Alerts of people who
 * left Radar since still count in the totals; the denominator is today's
 * Radar headcount.
 */
export function summarizeRadarAlerts(users, period) {
    let radarSubscribers = 0;
    let alerts = 0;
    let matches = 0;
    let delivered = 0;
    let alertedSubscribers = 0;
    for (const user of users) {
        const month = readRadarAlertMonth(user.radarAlerts, period);
        alerts += month.alerts;
        matches += month.matches;
        delivered += month.delivered;
        if (user.radarSubscriber) {
            radarSubscribers += 1;
            if (month.alerts > 0)
                alertedSubscribers += 1;
        }
    }
    return {
        period,
        radarSubscribers,
        alerts,
        matches,
        delivered,
        alertedSubscribers,
        alertsPerSubscriber: radarSubscribers > 0 ? alerts / radarSubscribers : null,
    };
}
/** `YYYY-MM` of the month before `period`. */
export function previousMonthKey(period) {
    const [year, month] = period.split("-").map(Number);
    if (!year || !month)
        return period;
    return month === 1 ? `${year - 1}-12` : `${year}-${String(month - 1).padStart(2, "0")}`;
}
//# sourceMappingURL=radar-baseline.js.map